<template>
    <span
        :style="style"
        class="animation"
    />
</template>

<script>
import lottie from 'lottie-web';
const rendererSettings = {
    scaleMode: 'centerCrop',
    clearCanvas: true,
    progressiveLoad: false,
    hideOnTransparent: true
};

import { url } from '@/helpers/utilities';

const removeExtension = /(^.\/)|(\.json$)/g;
const importAnimations = (imports) => {
    return Object.keys(imports)
        .reduce((animations, path) => {
            const name = path.replace('/assets/animations/', '').replace(removeExtension, '');
            const component = imports[path];
            animations[name] = component;
            return animations;
        }, {});
};
const IMPORT_MAP = importAnimations(import.meta.glob('@/assets/animations/**/*.json'));

export default {
    props: {
        src: { type: String, default: null, required: true },

        width: { type: [String, Number], default: -1 },
        height: { type: [String, Number], default: -1 },

        renderer: { type: String, default: 'svg' },
        speed: { type: Number, default: 1 },
        paused: { type: Boolean, default: false },
        loop: { type: Boolean, default: true },
        autoplay: { type: Boolean, default: true }
    },
    emits: ['load'],
    data() {
        return {
            animation: null
        };
    },
    computed: {
        style() {
            return {
                width: ~this.width ? `${this.width}px` : '100%',
                height: ~this.height ? `${this.height}px` : '100%'
            };
        },
        assets() {
            return IMPORT_MAP;
        }
    },
    watch: {
        src: {
            handler() {
                this.init();
            },
            immediate: true
        },
        paused: {
            handler(to) {
                if (!this.animation) return;
                this.animation.setSpeed(to ? 0 : this.speed);
                if (!to) this.animation.play();
            },
            immediate: true
        },
        speed(to) {
            if (!to) return;
            if (!this.animation) return;
            this.animation.setSpeed(to);
        }
    },
    methods: {
        getAnimation() {
            if (IMPORT_MAP[this.src]) {
                return IMPORT_MAP[this.src]().then((res) => res.default);
            } else {
                return this.$http.get(url(this.src), { withCredentials: false }).then(({ data }) => data);
            }
        },
        init() {
            if (this.animation) this.animation.destroy();
            return this.getAnimation().then(animationData => {
                this.animation = lottie.loadAnimation({
                    renderer: this.renderer,
                    loop: this.loop,
                    autoplay: this.autoplay && !this.paused,
                    container: this.$el,
                    animationData,
                    rendererSettings
                });
                this.animation.setSpeed(this.speed);
                this.$emit('load');
            });
        },
        play() {
            if (!this.animation) return this.$timeout(this.play, 100);
            this.animation.setDirection(1);
            this.animation.play();
        },
        replay() {
            this.animation.goToAndStop(0, true);
            this.play();
        },
        rewind() {
            this.animation.setDirection(-1);
            this.animation.play();
        },
        skipToLastFrame() {
            this.animation.goToAndStop(this.animation.totalFrames - 1, true);
        }
    }
};
</script>

<style lang="less">
span.animation {
    display: block;
    overflow: hidden;
    margin: 0 auto;
    transform: translateZ(0); // HACK: Safari
    svg {
        display: block;
        .primary {
            path { fill: var(--primary); }
        }
        .primary-contrast, .contrast {
            path {
                fill: var(--primary-contrast);
                stroke: var(--primary-contrast);
            }
        }
    }
}
</style>
